/** Headless reassignment form — assignment fields + optional comment. */

import { z } from 'zod'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { Textarea } from '@/frontend/components/ui/textarea'

import { useUpdateTask } from '../use-update-task'
import {
  hasAssignee,
  hasClinicWhenRequired,
  TASK_ASSIGNMENT_DEFAULTS,
  TaskAssignmentFieldGroup,
  taskAssignmentShape,
} from './task-assignment.field-group'

const reassignmentFormSchema = z
  .object({
    ...taskAssignmentShape,
    comment: z.string(),
  })
  .refine(hasAssignee, {
    message: 'Please select a team or staff member',
    path: ['assigneeRole'],
  })
  .refine(hasClinicWhenRequired, {
    message: 'Please select a clinic',
    path: ['assigneeClinicId'],
  })

type ReassignmentFormValues = z.input<typeof reassignmentFormSchema>

export function TaskReassignmentForm(props: {
  taskId: string
  onSuccess?: () => void
  onCancel?: () => void
}) {
  const { mutate, isPending } = useUpdateTask(props.taskId)

  const form = useAppForm({
    defaultValues: {
      ...TASK_ASSIGNMENT_DEFAULTS,
      comment: '',
    } as ReassignmentFormValues,
    validators: {
      onSubmit: reassignmentFormSchema,
    },
    onSubmit: ({ value, formApi }) => {
      mutate(
        {
          assigneeRole: value.assigneeRole,
          assigneeClinicId: value.assigneeClinicId,
          assigneeStaffId: value.assigneeStaffId,
          comment: value.comment.trim() || undefined,
        },
        {
          onSuccess: () => {
            form.reset()
            props.onSuccess?.()
          },
          onError: (err) => {
            formApi.setErrorMap({
              onSubmit: {
                form: err instanceof Error ? err.message : 'Failed to reassign task',
                fields: {},
              },
            })
          },
        }
      )
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4">
          <form.GlobalError />

          <TaskAssignmentFieldGroup
            form={form}
            fields={{
              assigneeRole: 'assigneeRole',
              assigneeClinicId: 'assigneeClinicId',
              assigneeStaffId: 'assigneeStaffId',
            }}
          />

          <form.Field name="comment">
            {(field) => (
              <Textarea
                className="min-h-[72px] resize-none"
                placeholder="Add a note about this reassignment… (optional)"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </div>

        <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <div className="flex items-center justify-end gap-2 pt-3">
              {props.onCancel && (
                <Button variant="outline" size="lg" type="button" onClick={props.onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="submit" size="lg" disabled={!canSubmit || isSubmitting || isPending}>
                {isPending ? 'Reassigning...' : 'Reassign'}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form.AppForm>
    </form>
  )
}
